import { useLocaleStore } from '../i18n';
import { getStore } from '../persistence/store';
import { appendAppLog } from '../platform/appLog';
import { useAppStore, warteAufExam } from '../store/useAppStore';
import { useToastStore } from '../ui/Toast';
import { installPreview } from './installPreview';
import { PreviewError } from './previewCrypto';
import { confirmPreview, fetchPreview, type PreviewCode } from './previewHttp';

/** Läuft schon eine Abholung? Ein zweiter Scan derselben Preview fände bei Message nichts mehr. */
let laeuft = false;

/**
 * Der eine Weg in eine Preview — QR-Scan, Web-Adresse und Desktop-Aufruf münden hier.
 *
 * Abholen, einspielen, quittieren, dann **direkt in den Bogen**: erste Station, Prüfer,
 * der eine Prüfling (Nutzer, 2026-10-25). Alle Stationen bleiben frei wählbar. Ohne PIN und
 * Rückfrage — wer den Code scannt, will genau das.
 *
 * ⚠️ **Eine laufende Bewertung wird nicht abgewiesen.** Der Wechsel in die Preview ist ein
 * gewöhnlicher Prüfungswechsel: `selectExam` sichert und zieht vorher ab wie sonst auch, und
 * nichts geht verloren.
 *
 * ⚠️ **Gelöscht wird von Hand** (Entscheidung des Nutzers) — anders als Ink räumt Stage die
 * Preview weder nach der Abgabe noch beim Start weg.
 */
export async function startPreview(code: PreviewCode): Promise<boolean> {
  if (laeuft) return false;
  laeuft = true;
  const t = useLocaleStore.getState().t;
  const melde = useToastStore.getState().zeigeText;
  try {
    melde(t.preview.laedt);
    const nachricht = await fetchPreview(code);
    const vorschau = await installPreview(getStore(), nachricht);
    // Erst wenn alles gespeichert ist: danach löscht Message, und ein zweites Abholen ginge nicht.
    await confirmPreview(code);
    await appendAppLog('Preview ' + vorschau.examId + ' von ' + code.url + ' eingespielt.');

    const z = useAppStore.getState();
    // Dieselbe Prüfung noch einmal: `selectExam` täte nichts, der neue Inhalt muss trotzdem herein.
    if (z.examId === vorschau.examId) await z.ladeInhaltNeu(vorschau.examId);
    else z.selectExam(vorschau.examId);
    const exam = await warteAufExam(vorschau.examId);
    if (!exam || exam.stations.length === 0) throw new PreviewError('format', 'Prüfung nicht lesbar');

    await useAppStore.getState().selectStation(exam.stations[0].id);
    useAppStore.getState().selectExaminer(vorschau.examinerId);
    if (useAppStore.getState().startup.kind === 'auswahl') useAppStore.getState().confirmSetup();
    await useAppStore.getState().pickCandidate(vorschau.candidateId);
    useToastStore.getState().hide();
    return true;
  } catch (fehler) {
    const grund = fehler instanceof PreviewError ? fehler.reason : null;
    melde(grund ? t.preview[grund] : t.preview.unbekannt);
    await appendAppLog('Preview von ' + code.url + ' gescheitert: ' + String(fehler), 'warn');
    return false;
  } finally {
    laeuft = false;
  }
}
